import React, { useContext } from "react";
import UserContext from "../../context/UserContext";
import CodeOfConductModal, { hasAcceptedCodeOfConduct } from "../modals/CodeOfConductModal";

// RecipientSelect: keeps a local selection to avoid being reset by parent re-renders
function RecipientSelect({ lobbyPlayers = [], currentUser, localRecipient, onRecipientChange }) {
  const onChange = (val) => {
    if (typeof onRecipientChange === 'function') onRecipientChange(val);
  };

  return (
    <div className="mb-2">
      <select aria-label="Recipient" value={localRecipient || "everyone"} onChange={(e) => onChange(e.target.value)} className="w-full border py-2 px-3 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700">
        <option value="everyone">Send to Everyone</option>
        {(lobbyPlayers || []).filter(p => !(currentUser && p.userId && currentUser.userId && p.userId === currentUser.userId)).map((p) => (
          <option key={p.userId || p.socketId} value={p.userId || p.socketId}>{`Send to ${p.username || p.userId || p.socketId}`}</option>
        ))}
      </select>
    </div>
  );
}

function LobbyChat({ lobbyMessages = [], chatInput, handleLobbyChatSend, handleLobbyChatInputChange, lobbyTypingPlayers, onBlur, lobbyPlayers = [], blockedUsers = [], selectedRecipient, setSelectedRecipient }) {

  const chatRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const { user: currentUser } = useContext(UserContext) || {};

  // Local recipient that persists across sends; only update from parent when a specific recipient is set
  const [localRecipient, setLocalRecipient] = React.useState(selectedRecipient || "everyone");
  React.useEffect(() => {
    if (selectedRecipient && selectedRecipient !== "everyone") {
      setLocalRecipient(selectedRecipient);
    }
  }, [selectedRecipient]);

  const playersByUserId = React.useMemo(() => {
    const m = {};
    (lobbyPlayers || []).forEach((p) => {
      if (p.userId) m[p.userId] = p.username || p.userId;
    });
    return m;
  }, [lobbyPlayers]);

  // When a recipient is preselected (e.g. from player menu), focus the chat input so user can type immediately
  React.useEffect(() => {
    try {
      const activeRecipient = localRecipient || selectedRecipient;
      if (activeRecipient && activeRecipient !== "everyone" && inputRef.current) {
        inputRef.current.focus();
      }
    } catch (e) {}
  }, [selectedRecipient, localRecipient]);

 

  React.useEffect(() => {
    // Wait for DOM update before checking scroll position
    const timer = setTimeout(() => {
      const chatDiv = chatRef.current;
      if (!chatDiv) return;
      // Check if scrollbar is at or near the bottom (allow 10px tolerance)
      const isAtBottom = chatDiv.scrollHeight - chatDiv.scrollTop - chatDiv.clientHeight < 50;
      if (isAtBottom) {
        chatDiv.scrollTop = chatDiv.scrollHeight;
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [lobbyMessages]);


  return (
  // <div className="mt-auto pt-2 px-2 sm:px-1 max-w-full overflow-x-auto">

    <div>
  <div ref={chatRef} className="border rounded p-2 mb-2 h-40 overflow-y-auto bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
  {lobbyMessages.filter(m => !(m.username && blockedUsers.includes(m.username))).map((msg, idx) => {
          const date = new Date(msg.datetime);
          const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
          if (msg.type === 'event') {
            return (
              <div key={idx} className="text-blue-700 dark:text-blue-300 italic" title={`${time}`}>
                {msg.text}
              </div>
            );
          }
          // Default: chat message
          if (msg.private) {
            // Determine whether current user is the sender or a recipient
            const fromId = msg.fromUserId || null;
            const toIds = Array.isArray(msg.toUserIds) ? msg.toUserIds : [];
            const isSender = currentUser && (currentUser.userId ? currentUser.userId === fromId : false);
            const isRecipient = currentUser && toIds.includes(currentUser.userId);

            let label = null;
            if (isSender) {
              // Build recipient display names
              const names = toIds.map((id) => playersByUserId[id] || id).filter(Boolean);
              label = names.length > 0 ? `[sent to ${names.join(", ")}]` : "[sent]";
            } else if (isRecipient) {
              label = "[sent to you]";
            }

            return (
              <div key={idx} className="text-black dark:text-gray-100">
                <span className="font-bold" title={`${time}`}>
                  {msg.fromUsername || msg.username || msg.fromUserId || 'Unknown'}
                  {label && <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">{label}</span>}
                </span>
                : {msg.text}
              </div>
            );
          }

          return (
            <div key={idx} className="text-black dark:text-gray-100">
              <span className="font-bold" title={`${time}`}>
                {typeof msg.username === 'string' ? msg.username : ''}
              </span> : {msg.text}
            </div>
          );
        })}
      </div>
      
  {/* Recipients are selected via player action menu; leaving this out to simplify UI */}

      {/* Recipient dropdown: options say 'Send to Everyone' or 'Send to {username}' */}
      {/* localRecipient persists across sends; notify parent only when user changes or on submit */}
      <RecipientSelect
        lobbyPlayers={lobbyPlayers}
        currentUser={currentUser}
        localRecipient={localRecipient}
        onRecipientChange={(val) => {
          setLocalRecipient(val);
          try { if (typeof setSelectedRecipient === 'function') setSelectedRecipient(val); } catch (e) {}
        }}
      />

      <CodeOfConductModal show={false} onClose={()=>{}} onAccept={()=>{}} />
      <form onSubmit={(e) => {
        e.preventDefault();
        try { if (typeof setSelectedRecipient === 'function') setSelectedRecipient(localRecipient); } catch (e) {}
        if (!hasAcceptedCodeOfConduct()){
          try { window.dispatchEvent(new CustomEvent('open-coc', { detail: { callback: () => { if (typeof handleLobbyChatSend === 'function') handleLobbyChatSend(localRecipient); } } })); } catch(e){}
          return;
        }
        if (typeof handleLobbyChatSend === 'function') handleLobbyChatSend(localRecipient);
        // After sending, restore focus to the input so user can continue typing
        try {
          setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
        } catch (e) {}
      }} className="flex mb-4">
        <input
          name="lobby-chat-input"
          type="text"
          value={chatInput}
          ref={inputRef}
          onChange={handleLobbyChatInputChange}
          onBlur={onBlur}
          className="border p-2 flex-1 mr-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400"
          placeholder="Type a message..."
          disabled={false}
          autoComplete="off"
          aria-autocomplete="list"
        />
  <button type="submit" className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 dark:bg-blue-700 dark:hover:bg-blue-600" title="Send Message">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.698 4.034L21 12L4.698 19.966a.5.5 0 0 1-.546-.124a.56.56 0 0 1-.12-.568L6.5 12L4.032 4.726a.56.56 0 0 1 .12-.568a.5.5 0 0 1 .546-.124M6.5 12H21"/></svg>
        </button>
      </form>
    </div>
  // </div>
)};

export default LobbyChat;
